/**
 * DOM helpers and an in-place HTML patcher.
 *
 * patch(container, markup) turns the container's existing children into the new markup while
 * re-using nodes where it can. Compared with `innerHTML = markup` this keeps:
 *   - keyboard focus and the caret position,
 *   - whatever the user has typed into a field the template did not change,
 *   - scroll positions of lists (chat, panels),
 *   - elements that other code owns, such as a live video stage or a tag input
 *     (mark with data-morph="skip": once the element exists, patch leaves it completely alone).
 *
 * Nodes are matched by `id` or `data-key` first, then by position and tag name.
 */

export const $ = (selector, root = document) => root.querySelector(selector);
export const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

const SKIP = 'skip';

function keyOf(node) {
  if (node.nodeType !== 1) return null;
  return node.getAttribute('data-key') || node.id || null;
}

function sameKind(a, b) {
  return a.nodeType === b.nodeType && a.nodeName === b.nodeName;
}

const FORM_VALUE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function syncAttributes(from, to) {
  for (const { name, value } of Array.from(to.attributes)) {
    if (from.getAttribute(name) !== value) from.setAttribute(name, value);
  }
  for (const { name } of Array.from(from.attributes)) {
    if (!to.hasAttribute(name)) from.removeAttribute(name);
  }
}

/**
 * Form controls: the template's `value` / `checked` / `selected` attributes are the source of truth only
 * when they CHANGE between renders. If the template did not change them, whatever the user typed stays.
 */
function syncFormState(from, to, before) {
  const tag = from.nodeName;
  if (tag === 'INPUT') {
    const type = (to.getAttribute('type') || 'text').toLowerCase();
    if (type === 'checkbox' || type === 'radio') {
      if (before.checked !== to.hasAttribute('checked')) from.checked = to.hasAttribute('checked');
    } else if (type !== 'file' && before.value !== to.getAttribute('value') && document.activeElement !== from) {
      from.value = to.getAttribute('value') ?? '';
    }
  } else if (tag === 'TEXTAREA') {
    const next = to.textContent;
    if (before.text !== next && document.activeElement !== from) from.value = next;
  } else if (tag === 'OPTION') {
    if (before.selected !== to.hasAttribute('selected')) from.selected = to.hasAttribute('selected');
  } else if (tag === 'SELECT') {
    const wanted = Array.from(to.options).find((o) => o.hasAttribute('selected'));
    if (wanted && before.selectedValue !== wanted.value) from.value = wanted.value;
  }
}

function snapshot(el) {
  const tag = el.nodeName;
  if (tag === 'INPUT') return { value: el.getAttribute('value'), checked: el.hasAttribute('checked') };
  if (tag === 'TEXTAREA') return { text: el.getAttribute('data-initial') ?? el.defaultValue };
  if (tag === 'OPTION') return { selected: el.hasAttribute('selected') };
  if (tag === 'SELECT') {
    const chosen = Array.from(el.options).find((o) => o.hasAttribute('selected'));
    return { selectedValue: chosen ? chosen.value : null };
  }
  return {};
}

function morph(from, to) {
  if (from.nodeType === 3 || from.nodeType === 8) {
    if (from.nodeValue !== to.nodeValue) from.nodeValue = to.nodeValue;
    return;
  }
  if (from.getAttribute('data-morph') === SKIP) return;
  const before = FORM_VALUE_TAGS.has(from.nodeName) || from.nodeName === 'OPTION' ? snapshot(from) : null;
  syncAttributes(from, to);
  morphChildren(from, to);
  if (before) syncFormState(from, to, before);
  if (from.nodeName === 'TEXTAREA') from.setAttribute('data-initial', to.textContent);
}

function morphChildren(fromParent, toParent) {
  const oldKids = Array.from(fromParent.childNodes);
  const keyed = new Map();
  const unkeyed = [];
  for (const kid of oldKids) {
    const key = keyOf(kid);
    if (key) keyed.set(key, kid);
    else unkeyed.push(kid);
  }
  const used = new Set();
  const newKids = Array.from(toParent.childNodes);
  newKids.forEach((next, index) => {
    const key = keyOf(next);
    let match = null;
    if (key) {
      match = keyed.get(key) || null;
      if (match && !sameKind(match, next)) match = null;
    } else {
      while (unkeyed.length && used.has(unkeyed[0])) unkeyed.shift();
      if (unkeyed.length && sameKind(unkeyed[0], next)) match = unkeyed.shift();
    }
    const slot = fromParent.childNodes[index] || null;
    if (match) {
      used.add(match);
      if (slot !== match) fromParent.insertBefore(match, slot);
      morph(match, next);
    } else {
      fromParent.insertBefore(next, slot);
      used.add(next);
    }
  });
  for (const kid of oldKids) {
    if (!used.has(kid) && kid.parentNode === fromParent) fromParent.removeChild(kid);
  }
}

/** Replace `container`'s content with `markup`, re-using existing nodes. */
export function patch(container, markup) {
  const template = document.createElement('template');
  template.innerHTML = String(markup);
  const target = container.cloneNode(false);
  target.append(template.content);
  morphChildren(container, target);
}

export function focusWithin(root, selector) {
  const el = root.querySelector(selector);
  if (el && typeof el.focus === 'function') el.focus();
  return el;
}

/** Scroll scrollable lists with data-stick-bottom to the end when they were already at the end. */
export function stickBottom(root, wasAtBottom) {
  for (const el of $$('[data-stick-bottom]', root)) {
    const key = el.id || el.getAttribute('data-key');
    if (!key || wasAtBottom.get(key) !== false) el.scrollTop = el.scrollHeight;
  }
}

export function recordScroll(root) {
  const map = new Map();
  for (const el of $$('[data-stick-bottom]', root)) {
    const key = el.id || el.getAttribute('data-key');
    if (key) map.set(key, el.scrollHeight - el.scrollTop - el.clientHeight < 40);
  }
  return map;
}
